import { z } from "zod";
import { IsoDateTime, OperatorEnvironment, Product } from "./common.ts";
import { Jwk } from "./jwks.ts";

// Port DTOs v1 (add-explorer-mvp design "Context map impact"): what crosses a module boundary
// through a synchronous port, serializable and strict (ADR 0001 R5). In process today; the same
// schemas become the HTTP/gRPC payloads after an extraction.

const Iso3166Country = z.string().regex(/^[A-Z]{2}$/);
/** ISO 3166-1 alpha-2 country (`BR`) or ISO 3166-2 subdivision (`BR-SP`). */
export const JurisdictionCode = z
  .string()
  .regex(/^[A-Z]{2}(-[A-Z0-9]{1,3})?$/)
  .meta({ id: "JurisdictionCode" });

/**
 * IP country of one address inside Location Intelligence (the IpRegistryDataset lookup, formerly
 * `ip-intelligence` → `core`; split-core-into-trust-contexts): v0 knows only the country (ADR 0004).
 */
export const IpAssessmentV1 = z
  .object({
    availability: z.enum(["available", "unavailable"]),
    /** Only when unavailable. */
    unavailable_cause: z.enum(["ip_not_allocated", "dataset_unavailable"]).optional(),
    /** Absent when the address has no information (private, reserved, unallocated). */
    location: z
      .object({
        country: Iso3166Country,
        subdivision: JurisdictionCode.optional(),
        municipality: z.string().optional(),
        postal_code: z.string().optional(),
        accuracy_km: z.number().positive().optional(),
      })
      .strict()
      .optional(),
    /** Always absent in v0; filled by a later IP intelligence database without a contract change. */
    network: z
      .object({
        isp: z.string().optional(),
        organization: z.string().optional(),
        connection_type: z
          .enum(["residential", "mobile", "corporate", "hosting", "unknown"])
          .optional(),
      })
      .strict()
      .optional(),
    ip_dataset_version: z.string().min(1),
  })
  .strict()
  .refine((a) => (a.availability === "unavailable") === (a.unavailable_cause !== undefined), {
    message: "unavailable_cause is required exactly when availability is unavailable",
  })
  .meta({ id: "IpAssessmentV1" });
export type IpAssessmentV1 = z.infer<typeof IpAssessmentV1>;

/** Portaria SPA/MF 722 items 32-33: validity and interval never above 30 minutes (design D18). */
export const MAX_REVALIDATION_SECONDS = 1800;
export const RevalidationWindowConfigV1 = z
  .object({
    valid_for_seconds: z.int().min(1).max(MAX_REVALIDATION_SECONDS),
    next_check_in_seconds: z.int().min(1).max(MAX_REVALIDATION_SECONDS),
    buffer_seconds: z.int().min(0),
  })
  .strict();

/**
 * Per-operator-environment check flags (add-explorer-mvp task 4.8): the `core` checks rolled out
 * per environment. A disabled required check is `not_evaluated` with `location_check_not_enabled`.
 */
export const CHECK_FLAG_VALUES = ["core.jurisdiction_check", "core.blocklist_check"] as const;
export const CheckFlagV1 = z.enum(CHECK_FLAG_VALUES).meta({ id: "CheckFlagV1" });
export type CheckFlagV1 = z.infer<typeof CheckFlagV1>;

/**
 * `operator-auth` → `core`, `explorer` (`getEnvironmentPolicy`): tiered retention, the Explorer's
 * highlight thresholds and the revalidation windows. The allowed jurisdictions, enabled checks and
 * maximum travel speed are the Decision Engine's Tenant Policy (`TenantPolicyV1`; removed from this
 * DTO by split-core-into-trust-contexts task 8.1, no consumer read them).
 */
export const EnvironmentPolicyV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    precise_location_days: z.int().positive().max(3650),
    raw_device_network_days: z.int().positive().max(3650),
    decision_retention_years: z.int().min(5).max(30),
    failure_rate_highlight: z.number().gt(0).max(1),
    shared_device_player_threshold: z.int().min(2),
    /** Per enabled product. */
    revalidation: z.partialRecord(Product, RevalidationWindowConfigV1),
  })
  .strict()
  .meta({ id: "EnvironmentPolicyV1" });
export type EnvironmentPolicyV1 = z.infer<typeof EnvironmentPolicyV1>;

/**
 * `explorer` → Location (`PreciseLocationReader.read`, `LocationFacade.readPrecise`): the operator and
 * environment come from the caller's verified console scope, never from request input; only that
 * operator environment's verifications are read (at most 10 000 ids per call).
 */
export const PreciseLocationsQueryV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    verification_ids: z.array(z.uuid()).max(10_000),
  })
  .strict()
  .meta({ id: "PreciseLocationsQueryV1" });
export type PreciseLocationsQueryV1 = z.infer<typeof PreciseLocationsQueryV1>;

/** Location → `explorer` (`PreciseLocationReader`): decrypted in process, never cached or stored. */
export const PreciseLocationV1 = z
  .object({
    verification_id: z.uuid(),
    source: z.literal("browser"),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy_m: z.number().nonnegative(),
  })
  .strict()
  .meta({ id: "PreciseLocationV1" });
export type PreciseLocationV1 = z.infer<typeof PreciseLocationV1>;

/**
 * `operator-auth` → `explorer` (`VerificationKeyDirectory.getPublicKey(kid)`), retired keys
 * included. Signing keys belong to an environment and a purpose, not to an operator.
 */
export const PublicKeyV1 = z
  .object({
    kid: z.string().min(1),
    environment: OperatorEnvironment,
    purpose: z.enum(["session", "verdict"]),
    status: z.enum(["pending", "active", "retired"]),
    jwk: Jwk.strict(),
  })
  .strict()
  .meta({ id: "PublicKeyV1" });
export type PublicKeyV1 = z.infer<typeof PublicKeyV1>;

/** Permission codes checked by the Explorer API; roles only bundle them (spec explorer-access). */
export const PERMISSIONS = [
  "explorer.read",
  "explorer.export",
  "blocklist.write",
  "notes.write",
  "users.manage",
  "audit.read",
  "location.precise.read",
  "configuration.manage",
  "credentials.manage",
  "platform.operators.manage",
  "platform.credentials.manage",
  "platform.fleet.manage",
] as const;
export const Permission = z.enum(PERMISSIONS).meta({ id: "Permission" });
export type Permission = z.infer<typeof Permission>;

/**
 * Platform-level permission codes of the Platform Administrator (design D20, spec
 * platform-administration): never granted per operator.
 */
export const PLATFORM_PERMISSIONS = [
  "platform.operators.manage",
  "platform.credentials.manage",
  // the Fleet tab, platform installation only (add-control-plane-console design D2)
  "platform.fleet.manage",
] as const satisfies readonly Permission[];
export const PlatformPermission = z.enum(PLATFORM_PERMISSIONS).meta({ id: "PlatformPermission" });
export type PlatformPermission = z.infer<typeof PlatformPermission>;

/** Permission codes that can be held on an operator (every code except the platform ones). */
export const OPERATOR_PERMISSIONS = [
  "explorer.read",
  "explorer.export",
  "blocklist.write",
  "notes.write",
  "users.manage",
  "audit.read",
  "location.precise.read",
  // the customer's Administrator: its operator's configuration and API credentials (D14)
  "configuration.manage",
  "credentials.manage",
] as const satisfies readonly Permission[];
export const OperatorPermission = z.enum(OPERATOR_PERMISSIONS).meta({ id: "OperatorPermission" });
export type OperatorPermission = z.infer<typeof OperatorPermission>;

export const ConsoleRole = z
  .enum(["viewer", "analyst", "administrator"])
  .meta({ id: "ConsoleRole" });
export const ConsoleLocale = z.enum(["en", "pt-BR"]).meta({ id: "ConsoleLocale" });

/**
 * `iam` → `explorer` (`ConsoleAccess.resolve(sessionId)`), resolved on every request: per allowed
 * operator the effective permission codes (role codes ∪ individual grants). The role is
 * informational and never checked. No email or other ConsoleUser data beyond ids. Platform
 * permissions are separate: a Platform Administrator has no operator access (design D20).
 */
export const ConsoleAccessContextV1 = z
  .object({
    console_user_id: z.uuid(),
    /** `null` for a Platform Administrator, who belongs to no organization (design D20). */
    organization_id: z.uuid().nullable(),
    locale: ConsoleLocale,
    operators: z.array(
      z
        .object({
          operator_id: z.uuid(),
          role: ConsoleRole,
          permissions: z.array(OperatorPermission),
        })
        .strict(),
    ),
    /** Platform-level codes (Platform Administrator only); empty for every other console user. */
    platform_permissions: z.array(PlatformPermission),
  })
  .strict()
  .meta({ id: "ConsoleAccessContextV1" });
export type ConsoleAccessContextV1 = z.infer<typeof ConsoleAccessContextV1>;

const AuditDetailValue = z.union([z.string().max(500), z.number(), z.boolean(), z.null()]);

/**
 * Why the system acted (adopt-installation-per-customer design D14 item 6, D15 item 5): the
 * closed list the access log shows with its label; `s_trust_support` is shown as the author
 * "S-Trust" (the organization, never a person).
 */
export const SystemCauseV1 = z
  .enum([
    "provisioning",
    "configure",
    "s_trust_support",
    "operator_deactivation",
    "integrity_check",
    "migration",
  ])
  .meta({ id: "SystemCauseV1" });
export type SystemCauseV1 = z.infer<typeof SystemCauseV1>;

/**
 * `iam` → `explorer` (`IamAuditReader.authorsOf`, task 6.11): the name and e-mail an access-log
 * record shows for its author, a console user of the caller's Organization.
 */
export const AuditAuthorDtoV1 = z
  .object({
    console_user_id: z.uuid(),
    display_name: z.string().max(400).nullable(),
    email: z.string().max(254),
  })
  .strict()
  .meta({ id: "AuditAuthorDtoV1" });
export type AuditAuthorDtoV1 = z.infer<typeof AuditAuthorDtoV1>;

/** `iam` → `explorer` (`IamAuditReader`): one audit record, free of secrets and tokens. */
export const AuditRecordDtoV1 = z
  .object({
    id: z.uuid(),
    occurred_at: IsoDateTime,
    /** `operator_auth`: credentials and operator configuration (task 6.11). */
    module: z.enum(["iam", "explorer", "operator_auth"]),
    actor: z.discriminatedUnion("type", [
      z
        .object({
          type: z.literal("console_user"),
          console_user_id: z.uuid(),
          /**
           * The author's name and e-mail (task 6.11), for a console user of the caller's
           * Organization; absent for anyone else.
           */
          display_name: z.string().max(400).nullable().optional(),
          email: z.string().max(254).optional(),
        })
        .strict(),
      z.object({ type: z.literal("system"), cause: SystemCauseV1.optional() }).strict(),
    ]),
    /** Absent for organization-level actions (e.g. identity provider changes). */
    operator_id: z.uuid().optional(),
    action: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    target: z.string().max(256),
    details: z.record(z.string(), AuditDetailValue),
    source_ip: z.union([z.ipv4(), z.ipv6()]).optional(),
  })
  .strict()
  .meta({ id: "AuditRecordDtoV1" });
export type AuditRecordDtoV1 = z.infer<typeof AuditRecordDtoV1>;

/** Registered (legal) name of an operator, as set in the Platform area (1-200 characters). */
export const OperatorNameV1 = z.string().min(1).max(200).meta({ id: "OperatorName" });

/** Status of an operator as `operator-auth` reports it to other modules. */
export const OperatorStatusV1 = z.enum(["active", "suspended", "deactivated"]);

/**
 * `operator-auth` → `iam` (`OperatorDirectory.createOperator`): the operator created by `iam`'s
 * `CreateOperatorInOrganization` (change onboard-operators-within-organizations, design D1).
 */
export const OperatorCreatedResultV1 = z
  .object({ operator_id: z.uuid() })
  .strict()
  .meta({ id: "OperatorCreatedResultV1" });
export type OperatorCreatedResultV1 = z.infer<typeof OperatorCreatedResultV1>;

/**
 * `operator-auth` → `iam` (`OperatorDirectory.findOperatorBySlug`): the operator holding a slug, so
 * that a repeated creation can complete an interrupted one (design D2).
 */
export const OperatorBySlugResultV1 = z
  .object({
    operator_id: z.uuid(),
    name: OperatorNameV1,
    status: OperatorStatusV1,
    /** The products enabled in the operator's environment (the provisioning Job's re-run, design D14). */
    enabled_products: z.array(Product).optional(),
  })
  .strict()
  .meta({ id: "OperatorBySlugResultV1" });
export type OperatorBySlugResultV1 = z.infer<typeof OperatorBySlugResultV1>;

/**
 * `operator-auth` → `iam` (`OperatorDirectory.operatorNames`): the registered names of the given
 * operators, for console labels (owner decision 2026-10-01), with each operator's status: `iam`'s
 * access resolution drops an operator reported as not `active` (a deactivated operator, until the
 * `OperatorDeactivated` consumer removed the access; add-console-account-settings design D9). An
 * unknown id is simply absent.
 */
export const OperatorNamesResultV1 = z
  .object({
    operators: z.array(
      z
        .object({
          operator_id: z.uuid(),
          name: OperatorNameV1,
          status: OperatorStatusV1,
        })
        .strict(),
    ),
  })
  .strict()
  .meta({ id: "OperatorNamesResultV1" });
export type OperatorNamesResultV1 = z.infer<typeof OperatorNamesResultV1>;

/**
 * `explorer` → `core` (`VerdictReader.read`, `core.getVerdict`): one verification of the caller's
 * verified operator environment (operator and environment never from request input).
 */
export const VerdictQueryV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    verification_id: z.uuid(),
  })
  .strict()
  .meta({ id: "VerdictQueryV1" });
export type VerdictQueryV1 = z.infer<typeof VerdictQueryV1>;

/**
 * `core` → `explorer` (`VerdictReader`, `core.getVerdict`): the signed result of one verification
 * of the operator (the verdict token exactly as returned by `POST /v1/verify`), read on demand by the
 * transaction detail to verify its signature (design D12, Diagram 21). Never logged, never in events.
 */
export const VerdictV1 = z
  .object({
    verification_id: z.uuid(),
    /** ES256 JWT (`typ` `strust-verdict+jwt`); its header carries the `kid`. */
    verdict_token: z.string().min(1),
  })
  .strict()
  .meta({ id: "VerdictV1" });
export type VerdictV1 = z.infer<typeof VerdictV1>;

const Iso3166Subdivision = z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/);

/**
 * `explorer` → Location (`AdministrativeAreaCatalog.list`, `LocationFacade.listAdministrativeAreas`) and the
 * query of `GET /explorer/v1/reference/administrative-areas`: without `country`, the countries of
 * the GeoBoundaryDataset; with it, that country's states (owner feedback 2026-10-01).
 */
export const AdministrativeAreasQueryV1 = z
  .object({ country: Iso3166Country.optional() })
  .strict()
  .meta({ id: "AdministrativeAreasQueryV1" });
export type AdministrativeAreasQueryV1 = z.infer<typeof AdministrativeAreasQueryV1>;

/** Natural Earth name of an area (English); absent when the dataset carries none (show the code). */
const AreaName = z.string().min(1).max(120);

/**
 * Location → `explorer`: the countries, or one country's states, that Location resolves locations to
 * (the GeoBoundaryDataset `boundary_dataset_version`), sorted by code. The Explorer's country and
 * state pickers list exactly these, so a selected value always matches what the checks use. An
 * unknown country has no states (empty list).
 */
export const AdministrativeAreasV1 = z
  .discriminatedUnion("level", [
    z
      .object({
        boundary_dataset_version: z.string().min(1),
        level: z.literal("country"),
        areas: z.array(z.object({ code: Iso3166Country, name: AreaName.optional() }).strict()),
      })
      .strict(),
    z
      .object({
        boundary_dataset_version: z.string().min(1),
        level: z.literal("state"),
        country: Iso3166Country,
        areas: z.array(z.object({ code: Iso3166Subdivision, name: AreaName.optional() }).strict()),
      })
      .strict(),
  ])
  .meta({ id: "AdministrativeAreasV1" });
export type AdministrativeAreasV1 = z.infer<typeof AdministrativeAreasV1>;
/** One country or state of an {@link AdministrativeAreasV1} listing. */
export type AdministrativeAreaOptionV1 = AdministrativeAreasV1["areas"][number];

/**
 * `fleet` → `iam` (`StepUpConfirmation.confirm`, adapted to `iam.confirmStepUp`; change
 * add-control-plane-console, design D2): who confirmed a change of a customer installation with
 * their password and current TOTP code. The Fleet tab records `console:<email>` as the requester or
 * approver; no secret crosses.
 */
export const StepUpConfirmedV1 = z
  .object({ console_user_id: z.uuid(), email: z.email().max(320) })
  .strict()
  .meta({ id: "StepUpConfirmedV1" });
export type StepUpConfirmedV1 = z.infer<typeof StepUpConfirmedV1>;

/** Why `iam` refused a step-up, in the port's vocabulary (each one refuses the change). */
export const STEP_UP_REFUSALS = [
  "invalid_credentials",
  "invalid_mfa_code",
  "account_locked",
  "step_up_rate_limited",
  "two_factor_not_enabled",
  "unauthenticated",
  "forbidden",
  "invalid_request",
  "server_error",
] as const;
export const StepUpRefusalV1 = z
  .object({
    code: z.enum(STEP_UP_REFUSALS),
    /** `account_locked` and `step_up_rate_limited`: seconds before a new attempt may succeed. */
    retry_after_seconds: z.int().positive().optional(),
  })
  .strict()
  .meta({ id: "StepUpRefusalV1" });
export type StepUpRefusalV1 = z.infer<typeof StepUpRefusalV1>;
